/**
 * useConversation hook - Manages conversation state and actions.
 *
 * Provides:
 * - Conversation turn management
 * - Message sending
 * - Loading states
 * - Topic-based dialogue completion tracking
 * - Structured input type tracking
 */

import { useState, useCallback, useMemo, useRef } from 'react'
import {
  getApiClient,
  type SendMessageRequest,
  type SendMessageResponse,
  type InputType,
  type InputOptions,
  type ImageOption,
} from '@/services/api'
import type { ConversationTurn } from '@/models'

/**
 * Topic coverage tracking (from API response)
 */
export interface TopicCoverage {
  topicsCovered: number
  topicsRequired: number
  coveredTopics: string[]  // Actual topic names (e.g., "current_values", "future_hopes")
}

/**
 * Conversation state
 */
export interface ConversationState {
  turns: ConversationTurn[]
  isLoading: boolean
  error: string | null
  isDialogueComplete: boolean
  topicCoverage: TopicCoverage
  pendingInputType: InputType
  pendingInputOptions: InputOptions | null
  imageOptions: ImageOption[] | null
  // Image confirmation flow
  awaitingImageConfirmation: boolean
  conversationSummary: string | null
  visionDescription: string | null
  // Graceful degradation when image generation not available
  imageGenerationUnavailable: boolean
  // Suggested next topic to explore
  nextTopicSuggestion: string | null
  // Explicit confirmation before ending conversation (issue #173)
  awaitingFinalConfirmation: boolean
}

/**
 * Conversation actions
 */
export interface ConversationActions {
  sendMessage: (content: string, inputMode?: 'text' | 'voice' | 'image', imageData?: string) => Promise<SendMessageResponse>
  setTurns: (turns: ConversationTurn[]) => void
  clearError: () => void
  clearFinalConfirmation: () => void
  reset: () => void
  /**
   * Transcribe audio via backend API.
   * Used for secure voice input (avoids exposing API keys in frontend).
   * @security Addresses issue #75
   */
  transcribeAudio: (audioBlob: Blob) => Promise<{ text: string; confidence: number }>
}

/**
 * useConversation hook return type
 */
export interface UseConversationReturn extends ConversationState, ConversationActions {}

/**
 * Total number of required topics for dialogue completion
 */
const REQUIRED_TOPICS_COUNT = 4

/**
 * Hook for managing conversation state
 */
export function useConversation(
  sessionId: string | null,
  participantId: string
): UseConversationReturn {
  const [turns, setTurns] = useState<ConversationTurn[]>([])
  const turnsRef = useRef<ConversationTurn[]>([])
  turnsRef.current = turns
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDialogueComplete, setIsDialogueComplete] = useState(false)
  const [pendingInputType, setPendingInputType] = useState<InputType>('text')
  const [pendingInputOptions, setPendingInputOptions] = useState<InputOptions | null>(null)
  const [imageOptions, setImageOptions] = useState<ImageOption[] | null>(null)
  const [awaitingImageConfirmation, setAwaitingImageConfirmation] = useState(false)
  const [conversationSummary, setConversationSummary] = useState<string | null>(null)
  const [visionDescription, setVisionDescription] = useState<string | null>(null)
  const [imageGenerationUnavailable, setImageGenerationUnavailable] = useState(false)
  const [nextTopicSuggestion, setNextTopicSuggestion] = useState<string | null>(null)
  const [awaitingFinalConfirmation, setAwaitingFinalConfirmation] = useState(false)
  const [serverTopicCoverage, setServerTopicCoverage] = useState<{
    covered_topics: string[]
    required_covered: number
    required_total: number
    all_required_met: boolean
  } | null>(null)

  const apiClient = getApiClient()

  // Use server-provided topic coverage when available, fallback to heuristic
  const topicCoverage = useMemo<TopicCoverage>(() => {
    if (serverTopicCoverage) {
      return {
        topicsCovered: serverTopicCoverage.required_covered,
        topicsRequired: serverTopicCoverage.required_total,
        coveredTopics: serverTopicCoverage.covered_topics,
      }
    }
    // Fallback heuristic (only used before first API response)
    const userTurnCount = turns.filter((t) => t.speaker === 'user').length
    const estimatedCovered = Math.min(userTurnCount, REQUIRED_TOPICS_COUNT)
    return {
      topicsCovered: estimatedCovered,
      topicsRequired: REQUIRED_TOPICS_COUNT,
      coveredTopics: [],
    }
  }, [turns, serverTopicCoverage])

  /**
   * Send a message in the conversation
   */
  const sendMessage = useCallback(
    async (content: string, inputMode: 'text' | 'voice' | 'image' = 'text', imageData?: string): Promise<SendMessageResponse> => {
      if (!sessionId) {
        throw new Error('No active session')
      }

      setError(null)

      // Create user turn immediately (optimistic update)
      const userTurnNumber = turnsRef.current.filter(t => t.speaker === 'user').length + 1
      const userTurn: ConversationTurn = {
        turn_id: `user-${Date.now()}`,
        session_id: sessionId,
        turn_number: userTurnNumber,
        speaker: 'user',
        content,
        input_mode: inputMode,
        timestamp: new Date().toISOString(),
        moderation_status: 'clean',
      }

      // Show user message immediately before API call
      setTurns((prev) => [...prev, userTurn])
      setIsLoading(true)

      try {
        const request: SendMessageRequest = {
          content,
          input_mode: imageData ? 'image' : inputMode,
          participant_id: participantId,
          image_data: imageData,
        }

        const response = await apiClient.sendMessage(sessionId, request)

        // Create system turn from response
        const systemTurn: ConversationTurn = {
          turn_id: response.turn_id,
          session_id: sessionId,
          turn_number: userTurnNumber,
          speaker: 'system',
          content: response.system_response,
          input_mode: 'system',
          timestamp: new Date().toISOString(),
          moderation_status: 'clean',
        }

        // Add system response (user turn already added)
        // Skip adding if dialogue_complete with images (response is just image generation details)
        // Note: We DO show the system turn for awaiting_image_confirmation - summary appears in chat
        const shouldSkipSystemTurn =
          response.dialogue_complete && response.image_options && response.image_options.length > 0

        if (!shouldSkipSystemTurn) {
          setTurns((prev) => [...prev, systemTurn])
        }

        // Check if awaiting image confirmation
        if (response.awaiting_image_confirmation) {
          setAwaitingImageConfirmation(true)
          setConversationSummary(response.conversation_summary ?? null)
          setVisionDescription(response.vision_description ?? null)
        }

        // Check if image generation is unavailable
        if (response.image_generation_unavailable) {
          setImageGenerationUnavailable(true)
          setAwaitingImageConfirmation(false)
        }

        // Check if dialogue is complete (images generated)
        // When awaiting_final_confirmation is true, defer setting isDialogueComplete
        // so the confirmation buttons render (they require !isDialogueComplete).
        if (response.dialogue_complete && !response.awaiting_final_confirmation) {
          setIsDialogueComplete(true)
          setAwaitingImageConfirmation(false)
          // Store image options for selection
          if (response.image_options) {
            setImageOptions(response.image_options)
          }
        }

        // Update pending input type for next question
        setPendingInputType(response.input_type || 'text')
        setPendingInputOptions(response.input_options || null)

        // Update topic coverage from server response
        if (response.topic_coverage) {
          setServerTopicCoverage(response.topic_coverage)
        }

        // Update next topic suggestion
        setNextTopicSuggestion(response.next_topic_suggestion || null)

        // Check if awaiting final confirmation before ending conversation (issue #173)
        if (response.awaiting_final_confirmation) {
          setAwaitingFinalConfirmation(true)
        } else {
          setAwaitingFinalConfirmation(false)
        }

        setIsLoading(false)
        return response
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
        setError(errorMessage)
        setIsLoading(false)
        throw err
      }
    },
    [sessionId, participantId, apiClient]
  )

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Clear final confirmation state so participant can type more (issue #173)
   */
  const clearFinalConfirmation = useCallback(() => {
    setAwaitingFinalConfirmation(false)
  }, [])

  /**
   * Reset conversation state
   */
  const reset = useCallback(() => {
    setTurns([])
    setIsLoading(false)
    setError(null)
    setIsDialogueComplete(false)
    setPendingInputType('text')
    setPendingInputOptions(null)
    setImageOptions(null)
    setAwaitingImageConfirmation(false)
    setConversationSummary(null)
    setVisionDescription(null)
    setServerTopicCoverage(null)
    setImageGenerationUnavailable(false)
    setNextTopicSuggestion(null)
    setAwaitingFinalConfirmation(false)
  }, [])

  /**
   * Transcribe audio via backend API.
   * @security Addresses issue #75 - keeps API credentials on backend
   */
  const transcribeAudio = useCallback(
    async (audioBlob: Blob): Promise<{ text: string; confidence: number }> => {
      if (!sessionId) {
        console.error('[useConversation] transcribeAudio called without active session')
        throw new Error('No active session for transcription')
      }

      console.info('[useConversation] Transcribing audio', {
        sessionId,
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
      })

      try {
        return await apiClient.transcribeAudio(sessionId, audioBlob)
      } catch (err) {
        console.error('[useConversation] transcribeAudio failed', {
          sessionId,
          blobSize: audioBlob.size,
          error: err instanceof Error ? err.message : err,
        })
        throw err
      }
    },
    [sessionId, apiClient]
  )

  return useMemo(() => ({
    turns,
    isLoading,
    error,
    isDialogueComplete,
    topicCoverage,
    pendingInputType,
    pendingInputOptions,
    imageOptions,
    awaitingImageConfirmation,
    conversationSummary,
    visionDescription,
    imageGenerationUnavailable,
    nextTopicSuggestion,
    awaitingFinalConfirmation,
    clearFinalConfirmation,
    sendMessage,
    setTurns,
    clearError,
    reset,
    transcribeAudio,
  }), [
    turns, isLoading, error, isDialogueComplete, topicCoverage,
    pendingInputType, pendingInputOptions, imageOptions,
    awaitingImageConfirmation, conversationSummary, visionDescription,
    imageGenerationUnavailable, nextTopicSuggestion, awaitingFinalConfirmation,
    clearFinalConfirmation, sendMessage, setTurns, clearError, reset, transcribeAudio,
  ])
}

export default useConversation
